import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { CashAdvanceAvailableEnum } from "shared/enums/CashAdvanceAvailable";
import { formatNoSignObject, signByCurrency } from "shared/helpers/BRLFormat";
import { ReactComponent as DistroIcon } from "shared/assets/images/icon-distro-colorido.svg";
import { ReactComponent as SpotifyIcon } from "shared/assets/images/icon-spotify.svg";
import { ReactComponent as AppleMusicIcon } from "shared/assets/images/icon-apple-music.svg";
import { ReactComponent as AmazonMusicIcon } from "shared/assets/images/icon-amazon-music.svg";
import { ReactComponent as YoutubeIcon } from "shared/assets/images/icon-youtube.svg";
import { ReactComponent as DeezerIcon } from "shared/assets/images/icon-deezer.svg";
import { ReactComponent as TidalIcon } from "shared/assets/images/icon-tidal.svg";
import { ReactComponent as SpikeArrow } from "shared/assets/images/spike-arrow.svg";

import { canAskAdvance } from "explorer/reducers/adiantamentos";
import { ArtistState } from "explorer/reducers/artistas";
import { DistributionStatusEnum } from "shared/enums/DistributionStatusEnum";
import { formatDateByLanguage } from "shared/helpers/date-format-helpers";
import { formatAdvanceMinimumAmount } from "shared/helpers/cash-advance-helpers";

const CardCTAPrincipal: React.FC<any> = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();

  const { userArtist } = useSelector((state: ArtistState) => state.artists);
  const { cashAdvanceAvailable } = useSelector((state: any) => state.advances);
  const { loggedUser } = useSelector((state: any) => state.users);

  const goRouteScrollTop = (route: string) => {
    history.push(route);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onOkModalDistro = async () => {
    history.push("/distribua");
  };

  const showCTADistroStrm = () =>
    userArtist?.distribution?.available ||
    userArtist?.distribution?.status === DistributionStatusEnum.Pending ||
    userArtist?.distribution?.status === DistributionStatusEnum.Signed;

  const mensagemSemAdiantamento = () => {
    if (
      cashAdvanceAvailable === CashAdvanceAvailableEnum.NotAvailable ||
      cashAdvanceAvailable === CashAdvanceAvailableEnum.NotFound
    )
      return t("pages.Adiantamentos.naoCumpreRequisitosMensagemSimples", {
        amount: formatAdvanceMinimumAmount({
          country: userArtist?.country,
          currency: userArtist?.cashAdvanceEstimated?.currency,
        }),
      });

    if (cashAdvanceAvailable === CashAdvanceAvailableEnum.NeedRetry)
      return t("pages.Adiantamentos.tentarNovamenteEmbreve");

    // if (cashAdvanceAvailable === CashAdvanceAvailableEnum.NotFound)
    //   return t("pages.Adiantamentos.spotifyNaoEncontrado");
  };

  if (canAskAdvance(cashAdvanceAvailable) || loggedUser?.isCashAdvanceAvailable)
    return (
      <div className="caixa-adiantamento padding">
        <div className="background" />
        <div className="container-linhas">
          <div className="linha linha-paragrafo">
            <p className="branco">
              <Trans
                i18nKey="pages.Adiantamentos.adianteReceitas"
                components={{ span: <span className="cor-destaque" /> }}
              />
            </p>
          </div>
          <div className="linha linha-paragrafo">
            <p className="valor">
              <b>
                {signByCurrency(userArtist?.cashAdvanceEstimated?.currency)}
              </b>
              {formatNoSignObject({
                ...userArtist?.cashAdvanceEstimated,
                amount:
                  (userArtist?.cashAdvanceEstimated?.amount ?? 1) /
                  (userArtist?.cashAdvanceEstimated?.trancheFactor ?? 1),
              })}
              {(userArtist?.cashAdvanceEstimated?.trancheFactor ?? 0) > 1 && (
                <>
                  {t("pages.Adiantamentos.ate")}
                  <b>
                    {signByCurrency(userArtist?.cashAdvanceEstimated?.currency)}
                  </b>
                  {formatNoSignObject(userArtist?.cashAdvanceEstimated)}
                </>
              )}
              {!userArtist.cashAdvanceEstimated?.calculatedOn && (
                <span
                  className={`disclaimer-dot ${
                    userArtist?.cashAdvanceEstimated?.trancheFactor === 1 &&
                    "green"
                  }`}
                >
                  *
                </span>
              )}
            </p>
          </div>
          {userArtist.cashAdvanceEstimated?.calculatedOn && (
            <div className="linha linha-paragrafo">
              <p>
                {t("pages.Adiantamentos.disclaimerCalculadoEm", {
                  data: formatDateByLanguage(
                    userArtist.cashAdvanceEstimated.calculatedOn,
                    i18n.language
                  ),
                  interpolation: { escapeValue: false },
                })}
              </p>
              <p>{t("pages.Adiantamentos.disclaimerValidade")}</p>
            </div>
          )}
          <div className={`linha actions`}>
            <button
              className="botao-roxo botao-adiantamento-desktop"
              onClick={() => goRouteScrollTop("/adiantamentos")}
            >
              {t("pages.Adiantamentos.solicitarAgora")}
            </button>
          </div>
          {!userArtist.cashAdvanceEstimated?.calculatedOn && (
            <div className={`linha disclaimer`}>
              <p className="roxo">
                {t("pages.Adiantamentos.valoresSujeitoAlteracoes")}
              </p>
            </div>
          )}
        </div>
      </div>
    );

  if (showCTADistroStrm()) {
    return (
      <div className="caixa-distro">
        <span className="branco">
          <DistroIcon />
          {t("pages.EstagioArtista.distoLiberadoTitulo")}
        </span>
        <div className="botoes-icones">
          <div className="icones">
            <AmazonMusicIcon />
            <SpotifyIcon />
            <YoutubeIcon />
            <TidalIcon />
            <AppleMusicIcon />
            <DeezerIcon />
          </div>
        </div>
        <div className="content-text">
          <SpikeArrow
            style={{
              position: "absolute",
              opacity: 0.5,
              top: -116,
              right: -29,
              width: "100%",
            }}
          />

          {userArtist?.distribution?.status ===
          DistributionStatusEnum.Signed ? (
            <div className="distro-content">
              <h3 className="heading-h3">
                {t("pages.EstagioArtista.distribuaAgora")}
              </h3>
              <p className="body-regular">
                {t("pages.EstagioArtista.distroLiberadoTexto")}
              </p>
            </div>
          ) : (
            <ul>
              <li className="circle-checkmark azul">
                {t("pages.EstagioArtista.distroAcessoAoLabels")}
              </li>
              <li className="circle-checkmark azul">
                {t("pages.EstagioArtista.distroIsencaoTaxaUpload")}
              </li>
              <li className="circle-checkmark azul">
                {t("pages.EstagioArtista.distrosMigracaoCatalogo")}
              </li>
            </ul>
          )}
          <div className="background" />
        </div>
        <div
          style={{
            margin: "24px 60px",
            display: "flex",
            flexDirection: "column",
            gap: 12,
            zIndex: 10,
            position: "relative",
          }}
        >
          <button className="botao-roxo" onClick={onOkModalDistro}>
            {t("pages.EstagioArtista.distribuaComStrm")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="caixa-adiantamento padding">
      <div className="background" />
      <span className="branco">
        {t("pages.EstagioArtista.adiantamentoNaoLiberadoTitulo")}
      </span>
      <p className="roxo">
        <Trans i18nKey={mensagemSemAdiantamento()} />
      </p>
      <button
        className="botao-roxo"
        onClick={() => goRouteScrollTop("/adiantamentos")}
      >
        {t("pages.EstagioArtista.saibaMais")}
      </button>
    </div>
  );
};

export default CardCTAPrincipal;
